import {
  OntologyFieldDefinition,
  OntologyValueType,
} from 'features/dynamicOntology'
import {
  ConditionalFormConfig,
  ConditionalFormSection,
  ConditionalFormTicketProps,
} from 'features/libra/stepComponents/conditionalForm/api/conditionalFormApi'
import { getValueWithDefault } from 'features/libra/stepComponents/conditionalForm/conditionalFormDefaults'
import { RecursivePropertyView } from 'features/objectViews'
import { Dispatch, FC, SetStateAction, useMemo } from 'react'
import styled from 'styled-components'

import { ExternalValueLookup } from './ExternalValueLookup'

interface ConditionalFormSectionProps {
  config: ConditionalFormSection
  ontologyFields: ConditionalFormConfig['ontologyFields']
  virtualObject: OntologyValueType | undefined
  setVirtualObject: Dispatch<SetStateAction<OntologyValueType | undefined>>
}

export function createVirtualObject(
  fieldValues: ConditionalFormTicketProps['fieldValues'],
  ontologyFields: ConditionalFormConfig['ontologyFields']
): OntologyValueType {
  return Object.fromEntries(
    Object.entries(ontologyFields).map(([key, fieldDefinition]) => [
      key.split('.')[0],
      getValueWithDefault(fieldValues?.[key], fieldDefinition),
    ])
  )
}

export const ConditionalFormSectionView: FC<ConditionalFormSectionProps> = ({
  config,
  ontologyFields,
  virtualObject,
  setVirtualObject,
}) => {
  const sectionFieldValues =
    (virtualObject as ConditionalFormTicketProps['fieldValues']) ?? {}

  const filteredOntologyFields = useMemo(() => {
    return Object.fromEntries(
      Object.entries(ontologyFields).filter(([key]) =>
        config.fields.includes(key)
      )
    )
  }, [config.fields, ontologyFields])
  const viewFields = useMemo<
    Record<string, [string, OntologyFieldDefinition]>
  >(() => {
    return Object.fromEntries(
      Object.entries(filteredOntologyFields).map(([key, fieldDef]) => [
        key.split('.')[0],
        [key, fieldDef],
      ])
    )
  }, [filteredOntologyFields])

  return (
    <SectionContainer>
      <RecursivePropertyView
        object={createVirtualObject(sectionFieldValues, filteredOntologyFields)}
        setObject={setVirtualObject}
        ontologyFields={viewFields}
        label={undefined}
        leafActions={({ field, setObject }) => {
          return setObject ? (
            <ExternalValueLookup field={field} setValue={setObject} />
          ) : undefined
        }}
      />
    </SectionContainer>
  )
}

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`
